import { useTranslation } from 'react-i18next';
import { PricingCardSeller, Typography } from '@/components';
import { OpportunityDetails } from '../../../types';
import { useSavePlan } from '../../../hooks/use-save-plan';

export type Plan = 'basic' | 'premium' | 'enterprise';

export type OpportunitySellerPricingCardsProps = {
  readonly opportunity: OpportunityDetails;
};

const OPPORTUNITY_STATUS = ['pending'];

export type FeatureItem = string | { text: string; subFeatures: string[] };

export function OpportunitySellerPricingCards({ opportunity }: OpportunitySellerPricingCardsProps) {
  const { t } = useTranslation(['opportunities']);
  const savePlan = useSavePlan(opportunity);

  const opportunityPlan = opportunity.plan;
  const isSelectionOpen = OPPORTUNITY_STATUS.includes(opportunity.status.name);

  const cardsInfo = [
    {
      plan: 'basic',
      price: '2.000',
      explanation: 'For simple listings that need solid visibility and essential support',
      title: 'Includes',
      features: [
        'Project listed on nTeaser for up to 6 months',
        'Information review and teaser preparation',
        '1 LinkedIn post + 2 launch emails to our investor network',
        'Basic support to manage incoming EOIs',
        {
          text: 'Access to a project performance dashboard',
          subFeatures: ['Free if you publish through an nTeaser Network Advisor'],
        },
      ] as FeatureItem[],
      button: 'Select plan',
    },
    {
      plan: 'premium',
      price: '5.000',
      explanation: 'Boost your exposure with priority visibility and weekly promotion',
      title: 'Everything in the Basic plan, plus',
      features: [
        'Top Listing placement for higher visibility',
        '1 extra featured LinkedIn post',
        'Weekly inclusion in our newsletter',
        'Priority support for faster responses',
      ] as FeatureItem[],
      button: 'Select plan',
    },
    {
      plan: 'enterprise',
      price: '10.000',
      explanation: 'Our most comprehensive option, ideal for sellers who want active outreach and dedicated support',
      title: 'Everything in the Premium plan, plus',
      features: [
        'Direct 1:1 outreach to selected investors',
        'Dedicated deal manager to handle interested buyers',
        'Full support in managing EOIs and investor follow-up',
        'Access to Market Insights (pricing, transactions, and market trends)',
      ] as FeatureItem[],
      button: 'Select plan',
    },
  ];

  return (
    <div className="rounded-lg border bg-white p-6 shadow-xs">
      <Typography as="h3" weight="semibold" size="title" className="uppercase">
        {t('Pricing Options')}
      </Typography>
      <Typography as="p" size="body-3" color="secondary" weight="normal" className="mt-1 mb-8">
        {t(
          "Choose your visibility level to reach the right investors. You can upgrade at any time to boost your project's exposure and accelerate the sale."
        )}
      </Typography>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
        {cardsInfo.map((card) => (
          <PricingCardSeller
            key={card.plan}
            plan={card.plan as Plan}
            price={card.price}
            title={card.title}
            explanation={card.explanation}
            features={card.features}
            isActive={card.plan === opportunityPlan}
            onSelect={(selectedPlan) => savePlan(selectedPlan)}
            isSelectable={isSelectionOpen}
            buttonText={card.button}
          />
        ))}
      </div>
    </div>
  );
}
